import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { ErrorService } from 'src/app/services/error.service';
import { environment } from 'src/environments/environment';
import * as E9EntosActions from '../actions/e9Entos.actions';
import { AppState } from '../app.reducer';
import { QueryParamsModel } from 'src/app/utils/query-params.model';
import { EntosSxediouModel, IEntosSxediou } from 'src/app/core/models/e9_entos_sxediou.model';

@Injectable()
export class E9EntosEffects {
    constructor(
        public actions$: Actions,
        public http: HttpClient,
        private errorService: ErrorService,
        private store: Store<AppState>
    ) { }
    payloadReload;
    private getKaekValue(row: any): string | null {
    const v = row['ΚΑΕΚ/ΚΩΔ. ΑΚΙΝΗΤΟΥ (27)'] ?? row['KAEK / ΚΩΔ. ΑΚΙΝΗΤΟΥ (27)'];
    return v == null ? null : String(v).trim();
    }

    private getKaekPrefix12(kaek: string | null): string | null {
    return kaek && kaek.length >= 12 ? kaek.slice(0, 12) : null;
    }

    private kadikLen(row: any): number {
    const k = row?.kadik == null ? null : String(row.kadik).trim();
    return k ? k.length : 0;
    }

    /** ENTOS rule:
     * For each 12-digit KAEK prefix:
     *  - if that prefix appears >1 times, keep only rows with kadik.length === 14
     *  - if it appears once, keep as is
     */
    applyEntosPrefixRule(rows: any[]): any[] {
    const counts = new Map<string, number>();
    for (const r of rows) {
        const pref = this.getKaekPrefix12(this.getKaekValue(r));
        if (!pref) continue;
        counts.set(pref, (counts.get(pref) ?? 0) + 1);
    }
    return rows.filter(r => {
        const pref = this.getKaekPrefix12(this.getKaekValue(r));
        if (!pref) return true;                  // keep rows with missing/short KAEK
        const cnt = counts.get(pref) ?? 0;
        return cnt > 1 ? this.kadikLen(r) === 14 : true;
    });
    }


    getE9Entos$ = createEffect(() =>
        this.actions$.pipe(
            ofType(E9EntosActions.gete9Entos),
            switchMap((payload) => {
                const query = { ...payload.payload, filters: JSON.stringify(payload.payload.filters) };
                this.store.dispatch(E9EntosActions.e9EntosLoading({ payload: { loading: true } }));
                this.payloadReload = payload.payload;
                const queryPayload = {
                    filters: query.filters,
                    sortOrder: query.sortOrder,
                    sortField: query.sortField,
                    pageNumber: query.pageNumber.toString(),
                    pageSize: query.pageSize.toString(),
                };
                return this.http
                    .get<{ data: IEntosSxediou[]; query: QueryParamsModel; totalCount: number }>(
                        environment.apiUrl + '/e9Entos',
                        {
                            params: queryPayload
                        }
                    )
                    .pipe(
                        switchMap((responseData) => {
                            const filtered = this.applyEntosPrefixRule(responseData.data || []);
                            this.store.dispatch(
                                E9EntosActions.gete9EntosSuccess({
                                    payload: {
                                    properties: filtered,
                                    totalCount: filtered.length
                                    },
                                })
                            );
                            return of(E9EntosActions.e9EntosLoading({ payload: { loading: false } }));
                        }),
                        catchError((error) => {
                            this.store.dispatch(
                            E9EntosActions.e9EntosFail({ payload: { error: this.errorService.mapError(error) } })
                            );
                            return of(E9EntosActions.e9EntosLoading({ payload: { loading: false } }));
                        })
                    );
            })
        )
    );

    updateE9Entos$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(E9EntosActions.updateE9Entos),
            switchMap(({ payload }) => {
                this.store.dispatch(
                    E9EntosActions.e9EntosLoading({ payload: { loading: true } })
                );
                return this.http
                    .put<null>(
                        environment.apiUrl + `/e9Entos/${payload.entity.id}`,
                        payload
                    )
                    .pipe(
                        switchMap(() => {
                            return of(
                                E9EntosActions.e9EntosUpdated({ payload: { entity: payload.entity } }),
                                E9EntosActions.e9EntosLoading({ payload: { loading: false } })
                            );
                        }),
                        catchError((error) => {
                            return of(
                                E9EntosActions.e9EntosFail({
                                    payload: { error: this.errorService.mapError(error) },
                                })
                            );
                        })
                    );
            })
        );
    });


    deleteE9Entos$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(E9EntosActions.deleteE9Entos),
            switchMap(({ payload }) => {
                this.store.dispatch(
                    E9EntosActions.e9EntosLoading({ payload: { loading: true } })
                );
                return this.http
                    .delete<null>(
                        environment.apiUrl + `/e9Entos/` + payload.entity.id,
                    )
                    .pipe(
                        switchMap(() => {
                            const entity: IEntosSxediou = new EntosSxediouModel();
                            entity.id = payload.entity.id;
                            return of(
                                E9EntosActions.e9EntosDeleted({ payload: { entity: entity } }),
                                E9EntosActions.e9EntosLoading({ payload: { loading: false } })
                            );
                        }),
                        catchError((error) => {
                            return of(
                                E9EntosActions.e9EntosFail({
                                    payload: { error: this.errorService.mapError(error) },
                                })
                            );
                        })
                    );
            })
        );
    });

    getE9EntosKaek$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(E9EntosActions.getE9EntosKaek),
            switchMap(({ payload }) => {
                this.store.dispatch(
                    E9EntosActions.e9EntosLoading({ payload: { loading: true } })
                );
                return this.http
                    .get<IEntosSxediou>(
                        `${environment.apiUrl}/e9Entos/${payload.kaekValue}`
                    )
                    .pipe(
                        mergeMap((e9Entos) => [
                            E9EntosActions.getE9EntosKaekSuccess({ payload: { e9Entos } }),
                            E9EntosActions.e9EntosLoading({ payload: { loading: false } })
                        ]),
                        catchError((error) => {
                            return of(
                                E9EntosActions.getE9EntosKaekFail({
                                    payload: { error: this.errorService.mapError(error) },
                                })
                            );
                        })
                    );
            })
        );
    });

    getE9EntosKadik$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(E9EntosActions.getE9EntosKadik),
            switchMap(({ payload }) => {
                this.store.dispatch(
                    E9EntosActions.e9EntosLoading({ payload: { loading: true } })
                );
                return this.http
                    .get<IEntosSxediou>(
                        `${environment.apiUrl}/e9Entos/${payload.kadikValue}`
                    )
                    .pipe(
                        mergeMap((e9Entos) => [
                            E9EntosActions.e9EntosLoading({ payload: { loading: false } })
                        ]),
                    );
            })
        );
    });



}
